import { type ItemsResponse, portalPath, useApiContext } from '@servicetim/ui/api';
import type { OptionLoader } from '@servicetim/ui/components';
import { hasTenantRole, useSessionClaims } from '@servicetim/ui/lib';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { tolerate } from './tolerate';

const PICKER_RESULTS = 20;

/**
 * `GET /v1/users?q=&ids=&limit=`: the people picker. Members and above only; guests get 403 (they
 * never pick tenant users), so the hooks stay disabled for them and callers hide the UI.
 */
export interface UserSummary {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  kind: string;
  email?: string;
}

/** True when the session may call the people picker at all. */
export function useCanLookupUsers(): boolean {
  const { tenantRole } = useSessionClaims();
  return hasTenantRole(tenantRole, 'member');
}

export function useUserSearch(q: string, options: { enabled?: boolean; limit?: number } = {}) {
  const { request, keys } = useApiContext();
  const allowed = useCanLookupUsers();
  const term = q.trim();
  return useQuery({
    queryKey: [...keys.all, 'portal', 'users', 'search', term, options.limit ?? 8],
    queryFn: ({ signal }) =>
      request<ItemsResponse<UserSummary>>('GET', portalPath('/users'), {
        query: { q: term, limit: options.limit ?? 8 },
        signal,
      }).then((r) => r.items),
    enabled: allowed && (options.enabled ?? true) && term.length > 0,
    staleTime: 30_000,
  });
}

/**
 * `SchemaForm`'s `pickers.users` loader for the portal: `GET /v1/users?q=&limit=` (members and
 * above only — the shared `UserPickerControl`'s default loader calls `GET /admin/v1/users`,
 * which 401s the portal's app-client token; the portal always overrides `pickers.users` with
 * this instead). Callers gate the picker itself behind `useCanLookupUsers()` for guests.
 */
export function usePortalUserOptionLoader(): OptionLoader {
  const { request } = useApiContext();
  return useCallback(
    async (query, signal) => {
      const items = await request<ItemsResponse<UserSummary>>('GET', portalPath('/users'), {
        query: { q: query.trim(), limit: PICKER_RESULTS },
        signal,
      }).then((r) => r.items);
      return items.map((u) => ({ id: u.id, label: u.displayName, description: u.email ?? null }));
    },
    [request],
  );
}

/** Resolves known user ids to names (comment authors, mentions); `{}` for guests. */
export function useUserLabels(ids: readonly string[]) {
  const { request, keys } = useApiContext();
  const allowed = useCanLookupUsers();
  const sorted = useMemo(() => [...new Set(ids)].sort(), [ids]);
  return useQuery({
    queryKey: [...keys.all, 'portal', 'users', 'labels', sorted],
    queryFn: ({ signal }) =>
      tolerate(
        () =>
          request<ItemsResponse<UserSummary>>('GET', portalPath('/users'), {
            query: { ids: sorted.join(','), limit: 50 },
            signal,
          }),
        { items: [] },
        [403, 404],
      ).then((r) => Object.fromEntries(r.items.map((u) => [u.id, u.displayName]))),
    enabled: allowed && sorted.length > 0,
    staleTime: 5 * 60_000,
  });
}
